import { useEffect, useRef, useState } from 'react';

import { StarRating } from './StarRating';
import { ReviewListItem } from './ReviewListItem';
import { useProfessionalReviews } from '../hooks/useProfessionalReviews';
import type { ProfessionalReview } from '../services/professional-reviews.service';

interface ReviewsSectionProps {
  professionalId: string;
  professionalName: string;
  rating: number;
  reviewCount: number;
}

function ReviewsSkeleton() {
  return (
    <div className="animate-pulse divide-y divide-white/10">
      {[0, 1, 2].map((i) => (
        <div key={i} className="flex items-start gap-3 py-4">
          <div className="h-10 w-10 shrink-0 rounded-full bg-white/10" />
          <div className="flex-1 space-y-2">
            <div className="h-4 w-1/3 rounded bg-white/10" />
            <div className="h-3 w-1/4 rounded bg-white/10" />
          </div>
        </div>
      ))}
    </div>
  );
}

export function ReviewsSection({
  professionalId,
  professionalName,
  rating,
  reviewCount,
}: ReviewsSectionProps) {
  const [page, setPage] = useState(1);
  const [reviews, setReviews] = useState<ProfessionalReview[]>([]);
  const mergedPageRef = useRef(0);

  const { data, isLoading, isFetching, isError } = useProfessionalReviews(professionalId, page);

  // Reset accumulation when navigating between profiles.
  useEffect(() => {
    setPage(1);
    setReviews([]);
    mergedPageRef.current = 0;
  }, [professionalId]);

  useEffect(() => {
    if (!data) return;
    // Guard against re-merging the same page twice while `keepPreviousData`
    // shows the prior page's payload during the next page's fetch.
    if (data.pagination.page <= mergedPageRef.current) return;
    mergedPageRef.current = data.pagination.page;
    setReviews((prev) =>
      data.pagination.page === 1 ? data.reviews : [...prev, ...data.reviews]
    );
  }, [data]);

  if (isError) return null;
  if (!isLoading && reviews.length === 0) return null;

  const hasMore = data?.pagination.hasMore ?? false;
  const isLoadingMore = isFetching && page > 1;

  return (
    <div className="rounded-2xl border border-white/10 bg-white/5 p-6">
      <h2 className="mb-4 text-lg font-semibold text-white">Recensioni</h2>
      <div className="mb-2 flex items-center gap-3">
        <span className="text-3xl font-bold text-white">{rating.toFixed(1)}</span>
        <StarRating rating={rating} size="md" />
        <span className="text-sm text-white/50">
          ({reviewCount} {reviewCount === 1 ? 'recensione' : 'recensioni'})
        </span>
      </div>

      {isLoading && reviews.length === 0 ? (
        <ReviewsSkeleton />
      ) : (
        <div className="divide-y divide-white/10">
          {reviews.map((review) => (
            <ReviewListItem key={review.id} review={review} professionalName={professionalName} />
          ))}
        </div>
      )}

      {hasMore && (
        <div className="mt-4 flex justify-center">
          <button
            type="button"
            onClick={() => setPage((p) => p + 1)}
            disabled={isLoadingMore}
            className="rounded-xl border border-white/15 bg-white/5 px-6 py-2.5 text-sm font-medium text-white/70 transition-colors hover:bg-white/10 disabled:cursor-not-allowed disabled:opacity-50"
          >
            {isLoadingMore ? 'Caricamento…' : 'Mostra altre recensioni'}
          </button>
        </div>
      )}
    </div>
  );
}
